import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { isEmpty, filter, find } from 'lodash';

import useEvent from '@src/hooks/useEvent';
import { isExpired, isBetween, getTimeRange } from '@src/utils/time';
import { getSelectedTicket } from '@src/utils/event';
import { DEFAULT_SLOT_TYPE, DEFAULT_TICKET_TYPE, DEFAULT_TICKET_NUMBER } from '@src/const/event';

import Dropdown from '@src/components/common/dropdown';

const MAX_PURCHASABLE_NUMBER = 20;

const DropdownsWrapper = styled.div`
	width: 100%;
`;

const PrimaryDropdown = styled.div`
	display: flex;
	align-items: center;
`;

const Label = styled.div`
	margin-right: 8px;
	font-size: 14px;
	color: #333;
`;

const StyledSlotDropdown = styled(Dropdown)`
	width: calc(100% - 36px);
`;

const SoldOutTag = styled.span`
	padding: 2px 4px;
	border-radius: 4px;
	border: 1px solid #DDDDDD;
	margin-right: 8px;
	font-size: 14px;
	color: #101010;
`;

const SecondaryDropdowns = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-column-gap: 20px;
	margin-top: 8px;

	@media ${props => props.theme.device.mobile} {
		display: block;
		margin-top: 16px;
	}
`;

const SecondaryDropdown = styled.div`
	display: flex;
	align-items: center;

	@media ${props => props.theme.device.mobile} {
		&:not(:first-child) {
			margin-top: 16px;
		}
	}
`;

const StyledTicketDropdown = styled(Dropdown)`
	width: calc(100% - 36px);
	.options {
		width: 100%
	}
	@media ${props => props.theme.device.lgDesktop} {
		.options {
			width: calc(100% + 203px);
		}
	}
	@media ${props => props.theme.device.desktop} {
		.options {
			width: calc(100% + 172px);
		}
	}
	@media ${props => props.theme.device.mobile} {
		&:not(:first-child) {
			margin-top: 16px;
		}
	}
`;

const getSlotTime = ({ selectedId, sortedEventSlots, t }) => {
	const selectedSlot = find(sortedEventSlots, slot => slot.id === selectedId);

	return getTimeRange({ startTime: selectedSlot.start_at, endTime: selectedSlot.end_at, t });
};

const getSlotOptionText = ({ showSoldOut, eventSlot, t }) => (
	<>
		<div>
			{showSoldOut && <SoldOutTag>{t('event:sold_out_tag')}</SoldOutTag>}
			<span>{eventSlot.name}</span>
		</div>
		<div>{getTimeRange({ startTime: eventSlot.start_at, endTime: eventSlot.end_at, t })}</div>
	</>
);

const getSlotOptions = ({ slotType, sortedEventSlots, t }) => sortedEventSlots.reduce((acc, eventSlot) => {
	const notYetOnSaleTickets = filter(eventSlot.ticket_info, ticket => isExpired({ startTime: ticket.saleStartTime }));
	const currentSaleTickets = filter(
		eventSlot.ticket_info,
		ticket => isBetween({ startTime: ticket.saleStartTime, endTime: ticket.saleEndTime }),
	);
	const isSaleEnd = !find(eventSlot.ticket_info, ticket => isExpired({ startTime: ticket.saleEndTime }));
	// Whether any tickets available for purchase currently are sold out or not.
	const isSoldOut = !isEmpty(currentSaleTickets) && !find(currentSaleTickets, ticket => ticket.remain > 0);
	/**
	 * Three scenario
	 * 1. All ticket will be on sale in the future.
	 * 2. Ticket whose sale time is current is sold out but another can be purchased in the future.
	 * 3. There are no ticket can be purchased currently but there are other tickets can be purchased in the future.
	 */
	const isNotYetOnSale = !find(eventSlot.ticket_info, ticket => isExpired({ endTime: ticket.saleStartTime })) || (
		isSoldOut && !isEmpty(notYetOnSaleTickets)
	) || (isEmpty(currentSaleTickets) && !isEmpty(notYetOnSaleTickets));
	const showSoldOut = isSaleEnd || isSoldOut;

	return isNotYetOnSale ? acc : [
		...acc,
		{
			type: eventSlot.id,
			text: getSlotOptionText({ showSoldOut, eventSlot, t }),
			isSelected: slotType === eventSlot.id,
			isGrayOut: showSoldOut,
		},
	];
}, []);

const getTicketOptionText = ({ showSoldOut, ticketName, t }) => (
	<>
		{showSoldOut && <SoldOutTag>{t('event:sold_out_tag')}</SoldOutTag>}
		<span>{ticketName}</span>
	</>
);

const getTicketOptions = ({ slotType, ticketType, sortedEventSlots, t }) => {
	const selectedSlot = find(sortedEventSlots, slot => slot.id === slotType);

	return isEmpty(selectedSlot) ? {} : selectedSlot.ticket_info.reduce((acc, ticketInfo) => {
		const isSaleEnd = isExpired({ endTime: ticketInfo.saleEndTime });
		// Whether any tickets available for purchase currently are sold out or not.
		const isSoldOut = isBetween({
			startTime: ticketInfo.saleStartTime,
			endTime: ticketInfo.saleEndTime,
		}) && (ticketInfo.remain < 1);
		const isNotYetOnSale = isExpired({ startTime: ticketInfo.saleStartTime });
		const showSoldOut = isSaleEnd || isSoldOut;

		return isNotYetOnSale ? acc : [
			...acc,
			{
				type: ticketInfo.id,
				text: getTicketOptionText({ showSoldOut, ticketName: ticketInfo.name, t }),
				isSelected: ticketType === ticketInfo.id,
				isGrayOut: showSoldOut,
			},
		];
	}, []);
};

const getTicketNumberOptions = ({ slotType, ticketType, ticketNumber, sortedEventSlots }) => {
	const selectedTicket = getSelectedTicket({ slotId: slotType, ticketId: ticketType, sortedEventSlots });

	if (isEmpty(selectedTicket)) return {};

	const maxNum = selectedTicket.remain > MAX_PURCHASABLE_NUMBER ? MAX_PURCHASABLE_NUMBER : selectedTicket.remain;

	return Array.from(Array(maxNum).keys()).map(num => ({
		type: num + 1,
		text: num + 1,
		isSelected: ticketNumber === (num + 1),
	}));
};

interface ISlotSelection {
	className?: string;
	slotType: string;
	ticketType: string;
	ticketNumber: number;
	dropdownError: {
		slotType: boolean;
		ticketType: boolean;
		ticketNumber: boolean;
	};
	handleSelectSlot: (type: string) => void;
	handleSelectTicket: (type: string) => void;
	handleSelectTicketNumber: (type: number) => void;
}

const Dropdowns = ({
	className, slotType, ticketType, ticketNumber, dropdownError,
	handleSelectSlot, handleSelectTicket, handleSelectTicketNumber,
}: ISlotSelection): JSX.Element => {
	const { t } = useTranslation();
	const { sortedEventSlots } = useEvent();

	const selectedSlotOption = (slotType === DEFAULT_SLOT_TYPE) ?
		t('event:please_select_slot') : getSlotTime({ selectedId: slotType, sortedEventSlots, t });
	const slotOptions = getSlotOptions({ slotType, sortedEventSlots, t });
	const selectedTicketOption = (ticketType === DEFAULT_TICKET_TYPE) ?
		t('event:please_select_ticket_type') :
		getSelectedTicket({ slotId: slotType, ticketId: ticketType, sortedEventSlots }).name;
	const ticketOptions = useMemo(
		() => getTicketOptions({ slotType, ticketType, sortedEventSlots, t }),
		[slotType],
	);
	const selectedTicketNumber = (ticketNumber === DEFAULT_TICKET_NUMBER) ?
		t('event:please_select_number') : ticketNumber;
	const ticketNumberOptions = getTicketNumberOptions({ slotType, ticketType, ticketNumber, sortedEventSlots });

	useEffect(() => {
		if ((slotType === DEFAULT_SLOT_TYPE) && (slotOptions.length === 1)) {
			handleSelectSlot(slotOptions[0].type);
		}
	}, [slotType, slotOptions]);

	useEffect(() => {
		if (ticketOptions.length === 1) {
			handleSelectTicket(ticketOptions[0].type);
		}
	}, [ticketOptions]);

	return (
		<DropdownsWrapper className={className}>
			<PrimaryDropdown>
				<Label>{t('event:slot')}</Label>
				<StyledSlotDropdown
					isError={dropdownError.slotType}
					selectedOption={selectedSlotOption}
					options={slotOptions}
					ellipsisOption={false}
					handleSelectOption={handleSelectSlot}
				/>
			</PrimaryDropdown>
			<SecondaryDropdowns>
				<SecondaryDropdown>
					<Label>{t('event:ticket_type')}</Label>
					<StyledTicketDropdown
						isDisabled={slotType === DEFAULT_SLOT_TYPE}
						isError={dropdownError.ticketType}
						selectedOption={selectedTicketOption}
						options={ticketOptions}
						ellipsisOption={false}
						handleSelectOption={handleSelectTicket}
					/>
				</SecondaryDropdown>
				<SecondaryDropdown>
					<Label>{t('event:number')}</Label>
					<StyledTicketDropdown
						isDisabled={ticketType === DEFAULT_TICKET_TYPE}
						isError={dropdownError.ticketNumber}
						selectedOption={selectedTicketNumber}
						options={ticketNumberOptions}
						handleSelectOption={handleSelectTicketNumber}
					/>
				</SecondaryDropdown>
			</SecondaryDropdowns>
		</DropdownsWrapper>
	);
};

export default Dropdowns;
